import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

interface Currency {
  code: string;
  name: string;
  symbol: string | null;
}

export const CurrencySettings = () => {
  const { data: ownerData, refetch: refetchOwnerData } = useQuery({
    queryKey: ["ownerData"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      const { data, error } = await supabase
        .from("owner_data")
        .select("*")
        .eq("owner_id", user.id)
        .single();

      if (error) throw error;
      return data;
    },
  });

  const { data: currencies } = useQuery<Currency[]>({
    queryKey: ["currencies"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("currencies")
        .select("*")
        .order("name");

      if (error) throw error;
      return data;
    },
  });

  const handleCurrencyChange = async (value: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      const { error } = await supabase
        .from("owner_data")
        .update({ currency: value })
        .eq("owner_id", user.id);

      if (error) throw error;

      toast.success("Devise mise à jour avec succès");
      refetchOwnerData();
    } catch (error) {
      console.error("Erreur lors de la mise à jour de la devise:", error);
      toast.error("Erreur lors de la mise à jour de la devise");
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Devise</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">Devise de l'interface</label>
          <Select
            value={ownerData?.currency || "FCFA"}
            onValueChange={handleCurrencyChange}
          >
            <SelectTrigger>
              <SelectValue placeholder="Sélectionnez une devise" />
            </SelectTrigger>
            <SelectContent>
              {currencies?.map((currency) => (
                <SelectItem key={currency.code} value={currency.code}>
                  {currency.name} ({currency.symbol})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-sm text-muted-foreground">
            Cette devise sera utilisée pour afficher tous les montants dans votre interface
          </p>
        </div>
      </CardContent>
    </Card>
  );
};